/**
 * MEL Hooks
 * Custom React Query hooks for MEL data management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import melService from '../services/melService';
import type {
  SmartSearchRequest,
  IssueMelRequest,
  ClearMelRequest,
} from '../types/mel';

// Query keys for cache management
export const melKeys = {
  all: ['mel'] as const,
  documents: () => [...melKeys.all, 'documents'] as const,
  activeMels: () => [...melKeys.all, 'active'] as const,
  activeMelsByAircraft: (aircraftId: number) => [...melKeys.activeMels(), 'aircraft', aircraftId] as const,
  activeMelsByTail: (tailNumber: string) => [...melKeys.activeMels(), 'tail', tailNumber] as const,
  expiringMels: (days: number) => [...melKeys.activeMels(), 'expiring', days] as const,
  search: (query: string, aircraftType: string) => [...melKeys.all, 'search', query, aircraftType] as const,
};

/**
 * Hook to fetch MEL documents
 */
export const useMelDocuments = () => {
  return useQuery({
    queryKey: melKeys.documents(),
    queryFn: () => melService.getMelDocuments(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to fetch all active MELs
 */
export const useActiveMels = () => {
  return useQuery({
    queryKey: melKeys.activeMels(),
    queryFn: () => melService.getAllActiveMels(),
    staleTime: 1 * 60 * 1000, // 1 minute - active MELs change frequently
  });
};

/**
 * Hook to fetch active MELs for a specific aircraft
 */
export const useActiveMelsByAircraft = (aircraftId: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: melKeys.activeMelsByAircraft(aircraftId),
    queryFn: () => melService.getActiveMelsByAircraft(aircraftId),
    enabled: enabled && !!aircraftId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Hook to fetch active MELs for an aircraft by tail number
 */
export const useActiveMelsByTailNumber = (tailNumber: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: melKeys.activeMelsByTail(tailNumber),
    queryFn: () => melService.getActiveMelsByTailNumber(tailNumber),
    enabled: enabled && !!tailNumber.trim(),
    staleTime: 1 * 60 * 1000,
  });
};

/**
 * Hook to fetch MELs expiring soon
 */
export const useExpiringMels = (days: number = 3) => {
  return useQuery({
    queryKey: melKeys.expiringMels(days),
    queryFn: () => melService.getExpiringMels(days),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for smart search - only runs when enabled
 * Use refetch() to manually trigger search
 */
export const useMelSmartSearch = (
  request: SmartSearchRequest | null,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: request ? melKeys.search(request.query, request.aircraftType) : ['mel', 'search', 'empty'],
    queryFn: () => {
      if (!request) throw new Error('No search request provided');
      return melService.smartSearch(request);
    },
    enabled: options?.enabled ?? false,
    staleTime: 0, // Always refetch on new search
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes (formerly cacheTime)
  });
};

/**
 * Hook to issue a new MEL
 */
export const useIssueMel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: IssueMelRequest) => melService.issueMel(request),
    onSuccess: (data) => {
      // Invalidate all active MEL queries
      queryClient.invalidateQueries({ queryKey: melKeys.activeMels() });
      // Specifically invalidate the aircraft's MELs
      queryClient.invalidateQueries({
        queryKey: melKeys.activeMelsByAircraft(data.aircraftId),
      });
    },
  });
};

/**
 * Hook to clear an MEL
 */
export const useClearMel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ melId, request }: { melId: number; request: ClearMelRequest }) =>
      melService.clearMel(melId, request),
    onSuccess: () => {
      // Invalidate all active MEL queries since we don't know which aircraft
      queryClient.invalidateQueries({ queryKey: melKeys.activeMels() });
    },
  });
};

/**
 * Hook to get count of active MELs for an aircraft (useful for badges)
 */
export const useActiveMelCount = (aircraftId: number) => {
  const { data: mels } = useActiveMelsByAircraft(aircraftId);
  return mels?.length ?? 0;
};
